import React, { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ArrowRight, Mail, Phone, Instagram } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { CONTACT } from "@/lib/translations";

const PinterestIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="8" y1="20" x2="12" y2="11" />
    <path d="M9.5 13.5C9 11.5 9.5 8.5 12 8.5c2 0 3.5 1.5 3.5 3.5s-1.5 3.5-3 3.5c-1 0-1.5-0.7-1.5-1.5" />
  </svg>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim())
      return false;
    if (form.name.length > 200 || form.message.length > 5000) return false;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    return emailOk;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(t.contact.form.validation);
      return;
    }
    setSubmitting(true);
    try {
      // 'website' is the honeypot — backend rejects when non-empty
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        website: form.website,
      });
      toast.success(t.contact.form.successTitle, {
        description: t.contact.form.successBody,
      });
      setForm({ name: "", email: "", message: "", website: "" });
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(t.contact.form.errorTitle, {
          description:
            "Te veel berichten in korte tijd. Probeer het later opnieuw.",
        });
      } else {
        toast.error(t.contact.form.errorTitle, {
          description: t.contact.form.errorBody,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white pt-32 md:pt-40 pb-24 md:pb-32" data-testid="contact-page">
      <Toaster position="top-center" richColors />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center gap-4 mb-8">
          <span className="w-10 h-[1px] bg-[#E11D2E]" />
          <span className="eyebrow">{t.contact.eyebrow}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              {t.contact.title}
            </h1>
            <p className="mt-6 text-base md:text-lg text-black/65 leading-relaxed max-w-md">
              {t.contact.subtitle}
            </p>

            <div className="mt-14 space-y-10">
              <div>
                <p className="eyebrow mb-3">{t.contact.info.emailLabel}</p>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="font-display text-xl md:text-2xl font-light hover:text-[#E11D2E] transition-colors flex items-center gap-3 group"
                  data-testid="contact-email-link"
                >
                  <Mail size={18} className="opacity-60 group-hover:opacity-100" />
                  {CONTACT.email}
                </a>
              </div>

              <div>
                <p className="eyebrow mb-3">{t.contact.info.phoneLabel}</p>
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  className="font-display text-xl md:text-2xl font-light hover:text-[#E11D2E] transition-colors flex items-center gap-3 group"
                  data-testid="contact-phone-link"
                >
                  <Phone size={18} className="opacity-60 group-hover:opacity-100" />
                  {CONTACT.phone}
                </a>
              </div>

              <div>
                <p className="eyebrow mb-4">{t.contact.info.socialLabel}</p>
                <div className="flex items-center gap-3">
                  <a
                    href={CONTACT.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-black/15 flex items-center justify-center hover:border-[#E11D2E] hover:text-[#E11D2E] transition-colors"
                    aria-label="Instagram"
                    data-testid="contact-instagram-link"
                  >
                    <Instagram size={18} />
                  </a>
                  <a
                    href={CONTACT.pinterest}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-black/15 flex items-center justify-center hover:border-[#E11D2E] hover:text-[#E11D2E] transition-colors"
                    aria-label="Pinterest"
                    data-testid="contact-pinterest-link"
                  >
                    <PinterestIcon size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="space-y-2"
              data-testid="contact-form"
              noValidate
            >
              {/* Honeypot — hidden from real users, bots will fill it */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  top: "auto",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                <label htmlFor="cf-website">Website (laat leeg)</label>
                <input
                  id="cf-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={onChange}
                />
              </div>

              <div className="pt-2 pb-1">
                <label className="eyebrow block mb-2" htmlFor="cf-name">
                  {t.contact.form.name}
                </label>
                <input
                  id="cf-name"
                  name="name"
                  type="text"
                  className="fd-input"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Jesse Feenstra"
                  data-testid="contact-input-name"
                  maxLength={200}
                  required
                />
              </div>

              <div className="pt-6 pb-1">
                <label className="eyebrow block mb-2" htmlFor="cf-email">
                  {t.contact.form.email}
                </label>
                <input
                  id="cf-email"
                  name="email"
                  type="email"
                  className="fd-input"
                  value={form.email}
                  onChange={onChange}
                  placeholder="naam@voorbeeld.com"
                  data-testid="contact-input-email"
                  required
                />
              </div>

              <div className="pt-6 pb-1">
                <label className="eyebrow block mb-2" htmlFor="cf-message">
                  {t.contact.form.message}
                </label>
                <textarea
                  id="cf-message"
                  name="message"
                  rows={5}
                  className="fd-input"
                  value={form.message}
                  onChange={onChange}
                  placeholder="…"
                  data-testid="contact-input-message"
                  maxLength={5000}
                  required
                />
              </div>

              <div className="pt-10">
                <button
                  type="submit"
                  disabled={submitting}
                  className="fd-btn disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {submitting ? t.contact.form.sending : t.contact.form.submit}
                  {!submitting && <ArrowRight size={14} />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
